/**
 * The kitchen, as words for somebody else's picture tool.
 *
 * People make their renders wherever they like -- Midjourney, an image model in a chat, a
 * friend with Photoshop -- and the one thing those tools need is a description that gets
 * the kitchen RIGHT: the shape, the run lengths, where the sink and the cooktop are, the
 * door profile, the colour, the stone. This writes that description from the plan, so the
 * render they bring back is of the kitchen they designed and not a kitchen like it.
 */
import * as room from './room.js'

const WALL = { back: 'back wall', left: 'left wall', right: 'right wall', front: 'facing wall' }

export function renderPrompt(plan, maker, sched) {
  const pick = (list, id) => list.find((x) => x.id === id) ?? list[0]
  const style = pick(maker.doorStyles, plan.doorStyleId)
  const colour = pick(maker.colours, plan.colourId)
  const bench = pick(maker.benchtops, plan.benchtopId)
  const splash = pick(maker.splashbacks, plan.splashbackId)
  const handle = pick(maker.handles, plan.handleId)
  // The room the kitchen is standing in. A picture tool given no floor invents one, and the
  // render comes back a kitchen you did not design in a room you have never seen.
  const floor = maker.floors ? pick(maker.floors, plan.floorId) : null
  const walls = maker.wallColours ? pick(maker.wallColours, plan.wallColourId) : null

  // Only the fittings this kitchen actually has: a brief that mentions an oven in a kitchen
  // with no oven gets back a picture of a kitchen with an oven in it.
  const roles = new Set(plan.runs.flatMap((r) => r.items)
    .map((i) => maker.cabinets.find((c) => c.id === i.specId)?.role).filter(Boolean))
  const fittings = [
    roles.has('rangehood') && maker.rangehoods
      && `${pick(maker.rangehoods, plan.rangehoodId).name.toLowerCase()} rangehood`,
    roles.has('dishwasher') && maker.dishwashers
      && `${pick(maker.dishwashers, plan.dishwasherId).name.toLowerCase()} dishwasher`,
    roles.has('oven') && maker.ovens
      && pick(maker.ovens, plan.ovenId).name.toLowerCase(),
  ].filter(Boolean)

  const shape = { single: 'a single-wall', galley: 'a galley', l: 'an L-shaped', u: 'a U-shaped' }[plan.shape] ?? 'a'
  const runs = plan.runs.filter((r) => r.kind === 'base').map((r) => {
    const w = room.segment(plan, r.wallId)
    const len = w ? Math.round(room.length(w) / 100) / 10 : 0
    const roles = r.items.map((i) => maker.cabinets.find((c) => c.id === i.specId)?.role).filter(Boolean)
    const has = [...new Set(roles)].filter((x) => ['sink', 'cooktop', 'fridge', 'oven', 'dishwasher'].includes(x))
    return `${WALL[r.wallId] ?? r.wallId} ${len}m${has.length ? ` with the ${has.join(', ')}` : ''}`
  })
  const drawers = plan.runs.flatMap((r) => r.items).reduce((n, i) => n + (maker.cabinets.find((c) => c.id === i.specId)?.drawers ?? 0), 0)
  const doors = plan.runs.flatMap((r) => r.items).reduce((n, i) => n + (maker.cabinets.find((c) => c.id === i.specId)?.doors ?? 0), 0)
  const tall = plan.runs.flatMap((r) => r.items).filter((i) => maker.cabinets.find((c) => c.id === i.specId)?.kind === 'tall').length

  const finish = colour.gloss > 0.4 ? 'high-gloss' : 'matt'
  const profile = { slab: 'flat slab doors', shaker: 'shaker-profile doors', vgroove: 'V-groove panelled doors' }[style.style] ?? 'doors'
  const handles = handle.style === 'none' ? 'handleless, with a routed finger pull'
    : `${handle.name.toLowerCase().replace(',', '')} handles`

  return [
    `Photorealistic interior photograph of ${shape} kitchen, Australian home, natural daylight from a window.`,
    `Layout: ${runs.join('; ')}. Overhead cabinets above the base runs with a rangehood over the cooktop.`,
    `Cabinetry: ${profile} in ${colour.name.toLowerCase()} (${colour.colour}), ${finish} finish, ${handles}; ${drawers} drawers and ${doors} doors${tall ? `, ${tall} tall unit${tall > 1 ? 's' : ''}` : ''}.`,
    `Benchtop: ${bench.name.toLowerCase()} (${bench.colour}), ${sched?.benchtopMetres ?? ''}m. Splashback: ${splash.colour ? splash.name.toLowerCase() : 'same stone as the benchtop'}.`,
    fittings.length ? `Fittings: ${fittings.join(', ')}.` : null,
    [floor && `Floor: ${floor.name.toLowerCase()} (${floor.colour}).`,
      walls && `Walls: ${walls.name.toLowerCase()} paint (${walls.colour}).`]
      .filter(Boolean).join(' '),
    `Eye-level camera, 24mm lens, the whole kitchen in frame, no people, no text, no clutter. Made by ${maker.name}.`,
  ].filter(Boolean).join('\n')
}
